import { useCallback } from "react";
import type { UseMutationOptions } from "react-query";
import { useMutation, useQuery } from "react-query";

import type {
  AvpError,
  LoginResponse,
  LoginVariables,
  RegisterCdpVariables,
  RegisterVariables,
  RegisterVariablesSuccess,
  VerifyUpdateEmailError,
  VerifyUpdateEmailResponse,
} from "@jti/fe-common";
import { extractUtmParams, getConfig, isUrlWhitelisted } from "@jti/fe-common";
import { AUTH_ROUTE_PATHS, type AuthRoutePath, type AuthSearch } from "@jti/fe-common/routes/auth-routes";

import { MUTATION_KEYS, QUERY_KEYS } from "../../constants/queryKeys";
import type { ResetPasswordRequest, ResetPasswordResponse, RestResponseType } from "../../services";
import { CasAuthApi } from "../../services";
import { BrowserStorage, User } from "../../utils";

const user = new User(new BrowserStorage());
type GrantAccess = {
  isGranted: boolean;
  isLoading: boolean;
};

type AuthenticationValidation = GrantAccess & {
  isError: boolean;
};

type AuthenticationValidationOptions = {
  refetchOnWindowFocus?: boolean;
  retry?: boolean;
};

type Unregister = {
  isLoading: boolean;
  unregister: (externalUserIdentifier?: string, ciamToken?: string) => Promise<unknown>;
};

type Register = {
  register: (data: RegisterVariables, dobFormat?: string) => Promise<RegisterVariablesSuccess>;
};

type RegisterCdp = {
  registerCdp: (data: RegisterCdpVariables) => Promise<unknown>;
};

type ResetConsumerPassword = {
  isLoading: boolean;
  resetConsumerPassword: (data: ResetPasswordRequest) => Promise<ResetPasswordResponse>;
};

type Login = {
  login: (data: LoginVariables) => Promise<RestResponseType<LoginResponse>>;
  error: AvpError | null;
  isLoading: boolean;
  isFailed: boolean;
  isSuccess: boolean;
};

type VerifyConsumer = {
  isLoading: boolean;
  verifyConsumer: (email: string) => Promise<{ statusCode: string }>;
};

type VerifyEmailUpdate = {
  isLoading: boolean;
  data: VerifyUpdateEmailResponse | undefined;
  error: VerifyUpdateEmailError | null;
  verifyEmailUpdate: (token: string) => Promise<VerifyUpdateEmailResponse>;
};

type GraniteLayerManager = {
  getCurrentLayer: () => string;
};

type GraniteAuthor = {
  layerManager: GraniteLayerManager;
};

type AuthHrefResolver = (options: { route: AuthRoutePath; search?: AuthSearch }) => string;

declare global {
  interface Window {
    Granite?: {
      author?: GraniteAuthor;
    };
  }
}

export const useGrantAccess = (
  returnUrl: string | undefined,
  resolveConfiguredAuthHref: AuthHrefResolver
): GrantAccess => {
  if (!resolveConfiguredAuthHref) {
    throw new Error("useGrantAccess requires an auth href resolver");
  }

  const { whitelistedDomains } = getConfig();
  const { ciam_token } = user.getUser();
  const { isLoading, isGranted, isError } = useAuthenticationValidation(ciam_token, {
    refetchOnWindowFocus: false,
    retry: false,
  });
  const currentAuthorMode = window.Granite?.author?.layerManager.getCurrentLayer() ?? "";

  if (!ciam_token || isError) {
    if (currentAuthorMode !== "Edit") {
      const effectiveReturnUrl = returnUrl || `${window.location.pathname}${window.location.search}`;
      const utmQuery = extractUtmParams(window.location.search);
      const search = new URLSearchParams(utmQuery);
      const isReturnUrlWhitelisted = isUrlWhitelisted(effectiveReturnUrl, whitelistedDomains);

      if (isReturnUrlWhitelisted) {
        search.append("returnUrl", effectiveReturnUrl);
      }

      const route = isReturnUrlWhitelisted ? AUTH_ROUTE_PATHS.login : AUTH_ROUTE_PATHS.entry;
      window.location.href = resolveConfiguredAuthHref({ route, search });
    }
  }

  return { isGranted, isLoading };
};

export const useAuthenticationValidation = (
  ciamToken?: string,
  options?: AuthenticationValidationOptions
): AuthenticationValidation => {
  const casAuthApi = CasAuthApi.init();
  const { isLoading, isSuccess, isError } = useQuery(
    QUERY_KEYS.authenticationValidation,
    () => casAuthApi.validate(ciamToken || ""),
    {
      enabled: Boolean(ciamToken),
      refetchOnWindowFocus: options?.refetchOnWindowFocus,
      retry: options?.retry,
    }
  );

  return {
    isGranted: Boolean(ciamToken) && isSuccess,
    isLoading,
    isError,
  };
};

export const useRegister = (): Register => {
  const casAuthApi = CasAuthApi.init();
  const callRegister = useMutation(
    MUTATION_KEYS.register,
    ({ registerData, dobFormat }: { registerData: RegisterVariables; dobFormat?: string }) =>
      casAuthApi.register(registerData, dobFormat)
  );

  const register = useCallback(
    async (data: RegisterVariables, dobFormat?: string) =>
      await callRegister.mutateAsync({ registerData: data, dobFormat }),
    [callRegister]
  );

  return { register };
};

export const useRegisterCdpUser = (): RegisterCdp => {
  const casAuthApi = CasAuthApi.init();
  const callRegister = useMutation(MUTATION_KEYS.register, (data: RegisterCdpVariables) =>
    casAuthApi.registerCdpUser(data)
  );
  const registerCdp = useCallback(
    async (data: RegisterCdpVariables) => await callRegister.mutateAsync(data),
    [callRegister]
  );

  return { registerCdp };
};

export const useResetConsumerPassword = (): ResetConsumerPassword => {
  const casAuthApi = CasAuthApi.init();
  const { isLoading, mutateAsync } = useMutation(MUTATION_KEYS.resetConsumerPassword, (data: ResetPasswordRequest) =>
    casAuthApi.resetConsumerPassword(data)
  );

  const resetConsumerPassword = useCallback(
    async (data: ResetPasswordRequest): Promise<ResetPasswordResponse> => await mutateAsync(data),
    [mutateAsync]
  );

  return {
    resetConsumerPassword,
    isLoading,
  };
};

export const useLogin = (): Login => {
  const casAuthApi = CasAuthApi.init();
  const callLogin = useMutation(MUTATION_KEYS.login, (variables: LoginVariables) => casAuthApi.login(variables));

  const login = useCallback(
    async (data: LoginVariables): Promise<RestResponseType<LoginResponse>> => await callLogin.mutateAsync(data),
    [callLogin]
  );

  return {
    login,
    error: (callLogin.error as AvpError | null) ?? null,
    isLoading: callLogin.isLoading,
    isFailed: callLogin.isError,
    isSuccess: callLogin.isSuccess,
  };
};

export const useUnregister = (): Unregister => {
  const { uuid } = user.getUser();
  const casAuthApi = CasAuthApi.init();
  const callUnregister = useMutation(
    MUTATION_KEYS.unregister,
    ({ externalUserIdentifier, ciamToken }: { externalUserIdentifier?: string; ciamToken?: string }) =>
      casAuthApi.unregister(externalUserIdentifier || uuid || "", ciamToken)
  );
  const unregister = useCallback(
    async (externalUserIdentifier?: string, ciamToken?: string) =>
      await callUnregister.mutateAsync({ externalUserIdentifier, ciamToken }),
    [callUnregister]
  );

  return { unregister, isLoading: callUnregister.isLoading };
};

export const useDeactivateAccount = () => {
  const { uuid } = user.getUser();
  const casAuthApi = CasAuthApi.init();

  const callDeactivate = useMutation(MUTATION_KEYS.deactivateAccount, () => casAuthApi.deactivateAccount(uuid));
  const deactivate = useCallback(async () => await callDeactivate.mutateAsync(), [callDeactivate]);

  return { deactivate, isLoading: callDeactivate.isLoading };
};

export const useVerifyConsumer = (): VerifyConsumer => {
  const casAuthApi = CasAuthApi.init();
  const { isLoading, mutateAsync } = useMutation(MUTATION_KEYS.verifyConsumer, (email: string) =>
    casAuthApi.verifyConsumer(email)
  );

  const verifyConsumer = useCallback(
    async (email: string): Promise<{ statusCode: string }> => await mutateAsync(email),
    [mutateAsync]
  );

  return {
    verifyConsumer,
    isLoading,
  };
};

export const useVerifyEmailUpdate = (
  options?: UseMutationOptions<VerifyUpdateEmailResponse, VerifyUpdateEmailError, string>
): VerifyEmailUpdate => {
  const casAuthApi = CasAuthApi.init();
  const { isLoading, data, error, mutateAsync } = useMutation(
    MUTATION_KEYS.verifyEmailUpdate,
    (token: string) => casAuthApi.verifyEmailUpdate(token),
    options
  );

  const verifyEmailUpdate = useCallback(
    async (token: string): Promise<VerifyUpdateEmailResponse> => await mutateAsync(token),
    [mutateAsync]
  );

  return {
    verifyEmailUpdate,
    isLoading,
    data,
    error: (error as VerifyUpdateEmailError | null) ?? null,
  };
};
export const useMagicLinkAuth = () => {
  const casAuthApi = CasAuthApi.init();
  const { isLoading, isError, mutateAsync, isIdle } = useMutation({
    mutationKey: [MUTATION_KEYS.magicLinkLogin],
    mutationFn: async ({ token }: { token: string }) =>
      casAuthApi.login({
        strategy: "one-time-limited-token",
        strategy_data: { token },
      }),
  });
  return {
    loginWithMagicLink: mutateAsync,
    isLoading,
    isError,
    isIdle,
  };
};
